/*
 * Copyright OpenSearch Contributors
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * OpenSearch 贡献者要求对本文件的贡献遵循 Apache-2.0 许可证或兼容的开源许可证。
 *
 * 修改版权归 OpenSearch 贡献者所有。详情请查看 GitHub 历史记录。
 */

/*
 * 本文件已获得 Elasticsearch B.V. 根据一个或多个贡献者许可协议的授权。
 * 请查看随本工作分发的 NOTICE 文件，以获取有关版权所有权的更多信息。
 * Elasticsearch B.V. 根据 Apache 许可证 2.0 版（“许可证”）将本文件授权给您；
 * 除非遵守许可证，否则您不得使用本文件。您可以在以下地址获取许可证副本：
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * 除非适用法律要求或书面同意，否则根据许可证分发的软件按“原样”分发，
 * 不附带任何形式的明示或暗示的保证和条件。请参阅许可证以了解有关权限和限制的具体规定。
 */

import dateMath from '@opensearch/datemath';
import moment, { LocaleSpecifier } from 'moment'; // eslint-disable-line import/named
import { timeUnits, timeUnitsPlural } from './time_units';
import { getDateMode, DATE_MODES } from './date_modes';
import { parseRelativeParts } from './relative_utils';
import { DurationRange, TimeUnitId, ShortDate } from '../types';

const ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

export const commonDurationRanges: DurationRange[] = [
  { start: 'now/d', end: 'now/d', label: '今天' },
  { start: 'now/w', end: 'now/w', label: '本周' },
  { start: 'now/M', end: 'now/M', label: '本月' },
  { start: 'now/y', end: 'now/y', label: '本年' },
  { start: 'now-1d/d', end: 'now-1d/d', label: '昨天' },
  { start: 'now/w', end: 'now', label: '本周至今' },
  { start: 'now/M', end: 'now', label: '本月至今' },
  { start: 'now/y', end: 'now', label: '本年至今' },
];

function cantLookup(timeFrom: string, timeTo: string, dateFormat: string) {
  const displayFrom = formatTimeString(timeFrom, dateFormat);
  const displayTo = formatTimeString(timeTo, dateFormat, true);
  return `${displayFrom} 至 ${displayTo}`;
}

function isRelativeToNow(timeFrom: ShortDate, timeTo: ShortDate) {
  const fromDateMode = getDateMode(timeFrom);
  const toDateMode = getDateMode(timeTo);
  const isLast =
    fromDateMode === DATE_MODES.RELATIVE && toDateMode === DATE_MODES.NOW;
  const isNext =
    fromDateMode === DATE_MODES.NOW && toDateMode === DATE_MODES.RELATIVE;
  return isLast || isNext;
}

export function formatTimeString(
  timeString: string,
  dateFormat: string,
  roundUp = false,
  locale: LocaleSpecifier = 'en'
) {
  const timeAsMoment = moment(timeString, ISO_FORMAT, true);
  if (timeAsMoment.isValid()) {
    return timeAsMoment.locale(locale).format(dateFormat);
  }

  if (timeString === 'now') {
    return '现在';
  }

  const tryParse = dateMath.parse(timeString, { roundUp: roundUp });
  if (moment.isMoment(tryParse)) {
    return `~ ${tryParse.locale(locale).fromNow()}`;
  }

  return timeString;
}

export function prettyDuration(
  timeFrom: ShortDate,
  timeTo: ShortDate,
  quickRanges: DurationRange[] = [],
  dateFormat: string
) {
  const matchingQuickRange = quickRanges.find(
    ({ start: quickFrom, end: quickTo }) => {
      return timeFrom === quickFrom && timeTo === quickTo;
    }
  );
  if (matchingQuickRange && matchingQuickRange.label) {
    return matchingQuickRange.label;
  }

  if (isRelativeToNow(timeFrom, timeTo)) {
    let timeTense;
    let relativeParts;
    if (getDateMode(timeTo) === DATE_MODES.NOW) {
      timeTense = '过去';
      relativeParts = parseRelativeParts(timeFrom);
    } else {
      timeTense = '未来';
      relativeParts = parseRelativeParts(timeTo);
    }
    const countTimeUnit = relativeParts.unit.substring(0, 1) as TimeUnitId;
    const countTimeUnitFullName =
      relativeParts.count > 1
        ? timeUnitsPlural[countTimeUnit]
        : timeUnits[countTimeUnit];
    let text = `${timeTense} ${relativeParts.count} ${countTimeUnitFullName}`;
    if (relativeParts.round && relativeParts.roundUnit) {
      text += ` 舍入到 ${timeUnits[relativeParts.roundUnit]}`;
    }
    return text;
  }

  return cantLookup(timeFrom, timeTo, dateFormat);
}

export function showPrettyDuration(
  timeFrom: ShortDate,
  timeTo: ShortDate,
  quickRanges: DurationRange[] = []
) {
  const matchingQuickRange = quickRanges.find(
    ({ start: quickFrom, end: quickTo }) => {
      return timeFrom === quickFrom && timeTo === quickTo;
    }
  );
  if (matchingQuickRange) {
    return true;
  }

  return isRelativeToNow(timeFrom, timeTo);
}
